import React, { useEffect, useState } from 'react';
import { Plus, Search, X, Trash2, Eye } from 'lucide-react';
import { Modal, Button, FormField, Input, Select, PageHeader } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import ConfirmModal from '../../components/ConfirmModal';
import { purchaseReturnService, purchaseService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

export default function PurchaseReturnsPage() {
  const [data,    setData]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');

  const [formOpen,    setFormOpen]    = useState(false);
  const [poSearch,    setPoSearch]    = useState('');
  const [poResults,   setPoResults]   = useState([]);
  const [selectedPO,  setSelectedPO]  = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [refundMethod,setRefundMethod]= useState('cash');
  const [reason,      setReason]      = useState('');
  const [notes,       setNotes]       = useState('');
  const [saving,      setSaving]      = useState(false);

  const [viewItem,     setViewItem]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  useEffect(() => { fetchData(); }, [page, search]); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await purchaseReturnService.getAll({ page, limit:20, search });
      setData(r.data.data||[]);
      setTotal(r.data.pagination?.total||0);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const searchPO = async (q) => {
    if (!q || q.length < 2) { setPoResults([]); return; }
    try {
      const r = await purchaseService.getAll({ search: q, limit:20 });
      // فقط received ها قابل برگشت هستند
      setPoResults((r.data.data||[]).filter(p => p.status === 'received'));
    } catch {}
  };

  const selectPO = (po) => {
    setSelectedPO(po);
    setPoResults([]);
    setPoSearch(po.poNumber);
    const returnable = po.items.filter(i => (i.quantity-(i.returnedQty||0)) > 0);
    setReturnItems(returnable.map(item => ({
      medicine:     item.medicine?._id || item.medicine,
      medicineName: item.medicineName || item.medicine?.commercialName,
      maxQty:       item.quantity - (item.returnedQty||0),
      unit:         item.unit,
      unitPrice:    item.unitPrice,
      quantity:     0,
    })));
  };

  const updateQty = (idx, val) =>
    setReturnItems(prev => prev.map((it,i) => i===idx ? { ...it, quantity: Math.min(Number(val), it.maxQty) } : it));

  const updatePrice = (idx, val) =>
    setReturnItems(prev => prev.map((it,i) => i===idx ? { ...it, unitPrice: Number(val) } : it));

  const totalReturn = returnItems.reduce((s,i) => s + (Number(i.unitPrice)||0)*(Number(i.quantity)||0), 0);
  const itemsToReturn = returnItems.filter(i => i.quantity > 0);

  const handleSubmit = async () => {
    if (!selectedPO) return toast.error('Select a purchase order first');
    if (itemsToReturn.length === 0) return toast.error('Enter quantity for at least one item');
    setSaving(true);
    try {
      await purchaseReturnService.create({
        purchaseId: selectedPO._id,
        items: itemsToReturn.map(i => ({ medicine: i.medicine, quantity: i.quantity, unitPrice: i.unitPrice })),
        refundMethod, reason, notes,
      });
      toast.success('Purchase return processed — inventory updated');
      setFormOpen(false);
      setSelectedPO(null); setPoSearch(''); setReturnItems([]);
      setReason(''); setNotes(''); setRefundMethod('cash');
      fetchData();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await purchaseReturnService.delete(deleteTarget._id);
      toast.success('Return cancelled, inventory restored');
      setDeleteTarget(null);
      fetchData();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setDeleting(false); }
  };

  const resetForm = () => {
    setFormOpen(false);
    setSelectedPO(null); setPoSearch(''); setPoResults([]); setReturnItems([]);
    setReason(''); setNotes(''); setRefundMethod('cash');
  };

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Purchase Returns" subtitle="Return medicines to suppliers"
        actions={<Button onClick={()=>setFormOpen(true)}><Plus size={16}/> New Return</Button>}/>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search by return#, PO# or supplier..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        {search && <button onClick={()=>setSearch('')} className="text-gray-400 hover:text-gray-600"><X size={14}/></button>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Return #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">PO #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Supplier</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Items</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Method</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? Array(5).fill(0).map((_,i)=>(
              <tr key={i}><td colSpan={8} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>
            )) : data.length===0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">No purchase returns found</td></tr>
            ) : data.map(row=>(
              <tr key={row._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs text-orange-700 font-medium">{row.returnNumber}</td>
                <td className="px-4 py-3 font-mono text-xs text-blue-700">{row.poNumber||'-'}</td>
                <td className="px-4 py-3 text-gray-700">{row.supplierName||row.supplier?.name||'-'}</td>
                <td className="px-4 py-3 text-center text-gray-500">{row.items?.length}</td>
                <td className="px-4 py-3 text-right font-semibold text-red-600">{formatCurrency(row.totalAmount)}</td>
                <td className="px-4 py-3 text-gray-500 capitalize">{row.refundMethod}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{toJalali(row.createdAt)}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={()=>purchaseReturnService.getById(row._id).then(r=>setViewItem(r.data.data))}
                      className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600"><Eye size={13}/></button>
                    <button onClick={()=>setDeleteTarget(row)}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total>20 && (
          <div className="flex justify-between items-center px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,total)} of {total}</p>
            <div className="flex gap-1">
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40">Prev</button>
              <button onClick={()=>setPage(p=>p+1)} disabled={page*20>=total} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* New Return Modal */}
      <Modal isOpen={formOpen} onClose={resetForm} title="New Purchase Return" size="xl">
        <div className="space-y-4">
          {/* Step 1: Search PO */}
          <FormField label="Search Purchase Order (PO# or supplier name)" required>
            <div className="relative">
              <input value={poSearch}
                onChange={e=>{setPoSearch(e.target.value);searchPO(e.target.value);}}
                placeholder="Type PO number or supplier name (only received POs)..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              {poResults.length>0 && (
                <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                  {poResults.map(po=>(
                    <button key={po._id} type="button" onClick={()=>selectPO(po)}
                      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0">
                      <p className="text-sm font-semibold text-blue-700">{po.poNumber}</p>
                      <p className="text-xs text-gray-400">{po.supplier?.name||'-'} · {toJalali(po.saleDate||po.createdAt)} · {formatCurrency(po.totalAmount)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </FormField>

          {/* Step 2: Items */}
          {selectedPO && (
            <>
              <div className="bg-blue-50 rounded-xl p-3 text-sm flex gap-4 flex-wrap">
                <div><span className="text-gray-400 text-xs">PO #</span><p className="font-mono text-blue-700 font-bold">{selectedPO.poNumber}</p></div>
                <div><span className="text-gray-400 text-xs">Supplier</span><p>{selectedPO.supplier?.name||'-'}</p></div>
                <div><span className="text-gray-400 text-xs">Date</span><p>{toJalali(selectedPO.saleDate||selectedPO.createdAt)}</p></div>
                <div><span className="text-gray-400 text-xs">Total</span><p className="font-semibold">{formatCurrency(selectedPO.totalAmount)}</p></div>
              </div>

              {returnItems.length === 0 ? (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">All items in this PO have already been returned.</p>
              ) : (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Select Items to Return</p>
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Medicine</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Available</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Return Qty</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Unit Price</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Refund</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {returnItems.map((item, idx)=>(
                          <tr key={idx} className={item.quantity>0?'bg-orange-50':''}>
                            <td className="px-3 py-2 font-medium text-gray-800">{item.medicineName}</td>
                            <td className="px-3 py-2 text-center text-gray-500">{item.maxQty} {item.unit}</td>
                            <td className="px-3 py-2 text-center">
                              <input type="number" min="0" max={item.maxQty} value={item.quantity}
                                onChange={e=>updateQty(idx, e.target.value)}
                                className="w-20 text-center border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"/>
                            </td>
                            <td className="px-3 py-2">
                              <input type="number" min="0" step="0.01" value={item.unitPrice}
                                onChange={e=>updatePrice(idx, e.target.value)}
                                className="w-24 text-right border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"/>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-orange-600">
                              {item.quantity>0 ? formatCurrency(item.unitPrice*item.quantity) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                        <tr>
                          <td colSpan={4} className="px-3 py-2 font-semibold text-gray-600">Total Refund</td>
                          <td className="px-3 py-2 text-right font-bold text-orange-600">{formatCurrency(totalReturn)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Refund Method">
                  <Select value={refundMethod} onChange={e=>setRefundMethod(e.target.value)}>
                    <option value="cash">Cash Refund</option>
                    <option value="deduction">Deduct from Debt</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="credit">Credit</option>
                  </Select>
                </FormField>
                <FormField label="Reason">
                  <Select value={reason} onChange={e=>setReason(e.target.value)}>
                    <option value="">Select reason</option>
                    <option value="damaged">Damaged / Defective</option>
                    <option value="wrong_item">Wrong Item</option>
                    <option value="expired">Expired</option>
                    <option value="excess_quantity">Excess Quantity</option>
                    <option value="other">Other</option>
                  </Select>
                </FormField>
                <div className="col-span-2">
                  <FormField label="Notes">
                    <Input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Additional notes..."/>
                  </FormField>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} loading={saving}
              disabled={itemsToReturn.length===0}
              className="bg-orange-600 hover:bg-orange-700 text-white">
              Process Return ({formatCurrency(totalReturn)})
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={!!viewItem} onClose={()=>setViewItem(null)} title={`Return — ${viewItem?.returnNumber}`} size="lg">
        {viewItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm bg-gray-50 rounded-xl p-3">
              <div><span className="text-xs text-gray-400">Return #</span><p className="font-mono text-orange-700 font-bold">{viewItem.returnNumber}</p></div>
              <div><span className="text-xs text-gray-400">PO #</span><p className="font-mono text-blue-700">{viewItem.poNumber}</p></div>
              <div><span className="text-xs text-gray-400">Supplier</span><p>{viewItem.supplierName||'-'}</p></div>
              <div><span className="text-xs text-gray-400">Refund Method</span><p className="capitalize">{viewItem.refundMethod}</p></div>
              <div><span className="text-xs text-gray-400">Reason</span><p className="capitalize">{viewItem.reason||'-'}</p></div>
              <div><span className="text-xs text-gray-400">Date</span><p>{toJalali(viewItem.createdAt)}</p></div>
            </div>
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Medicine</th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Qty</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Unit Price</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Total</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {viewItem.items?.map((item,i)=>(
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium">{item.medicineName}</td>
                      <td className="px-3 py-2 text-center">{item.quantity} {item.unit}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-orange-600">{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 font-semibold">Total Refund</td>
                    <td className="px-3 py-2 text-right font-bold text-orange-600">{formatCurrency(viewItem.totalAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {viewItem.notes && <p className="text-xs text-gray-500">Notes: {viewItem.notes}</p>}
            <div className="flex justify-end">
              <Button variant="secondary" onClick={()=>setViewItem(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal isOpen={!!deleteTarget} onClose={()=>setDeleteTarget(null)}
        onConfirm={handleDelete} loading={deleting}
        title="Cancel Purchase Return"
        message={`Cancel return "${deleteTarget?.returnNumber}"? Inventory will be restored.`}
        confirmLabel="Cancel Return"/>
    </div>
  );
}
